import { FC } from 'react';
import { FormControl, FormLabel } from 'react-bootstrap';
import Skeleton from 'components/base/Skeleton';
import ErrorBox from 'components/ErrorBox';
import FormGroup from 'components/FormGroup';
import { SameDeliveryAndResidence } from 'components/forms/DeliveryAddress';
import WeakNetPrompt from 'components/forms/WeakNetPrompt';
import SelectFormutil from 'components/Formutil/SelectFormutil';
import { useProvinceList } from 'hooks/useGetCountryList';
import config from 'utils/config';
import { isEmptyValue } from 'utils/helper';
import { printf } from 'utils/i18n';
import { BaseHomeAddressProps } from '../../type';

export interface BaseAddressProps extends BaseHomeAddressProps {
    addressDisabled?: boolean; // 是否禁用地址
    postalDisabled?: boolean; // 是否禁用邮编
    hideMailAddress?: boolean; // 是否隐藏邮寄地址与居住地址不一致勾选框，默认展示
    legalResidenceCountry?: string;
}

const validMessages: Record<string, { required: string; format: string; maxLength?: string }> = {
    address: {
        required: config.inputAError(__('address'), true),
        format: __('You have entered the wrong format.'),
        maxLength: printf(__('地址过长，不超过%s个字符'), 180)
    },
    city: {
        required: config.inputAError(__('city'), false),
        format: __('You have entered the wrong format.'),
        maxLength: printf(__('城市过长，不超过%s个字符'), 40)
    },
    zip: {
        required: config.inputAError(__('zip code'), false),
        format: __('You have entered the wrong format.')
    }
};

const BaseAddress: FC<BaseAddressProps> = ({
    disabled,
    addressDisabled,
    postalDisabled,
    hideMailAddress,
    label = __('永久居住地址'),
    prefix,
    legalResidenceCountry
}) => {
    const { data: dataCANProvinces, isLoading, error, mutate } = useProvinceList(legalResidenceCountry);
    const isResCountryCAN = legalResidenceCountry === 'CAN';
    const disabledAddress = addressDisabled ?? disabled;
    const disabledPostal = postalDisabled ?? disabled;

    if (error) {
        return (
            <div className="mb-3">
                <ErrorBox error={error} onClick={() => mutate()} />
                {/* loading/error状态下不能直接提交,需要拦截一下 */}
                <WeakNetPrompt name="temporary_can_provinces" type="error" />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="mb-3">
                <Skeleton line={2} />
                {/* loading状态下不能直接提交,需要拦截一下 */}
                <WeakNetPrompt name="temporary_can_provinces" type="loading" />
            </div>
        );
    }

    return (
        <>
            {label && <FormLabel>{label}</FormLabel>}
            <FormGroup
                label={__('Street Address')}
                name={`${prefix}_detail`}
                checker={value => {
                    if (disabledAddress) {
                        return true;
                    }

                    if (!value) {
                        return validMessages.address.required;
                    }

                    if (!value.match(/^[a-zA-Z0-9 ./-]*'?[a-zA-Z0-9 ./-]*$/)) {
                        return validMessages.address.format;
                    }

                    if (value.length > 180) {
                        return validMessages.address.maxLength;
                    }

                    return true;
                }}>
                <FormControl disabled={disabledAddress} placeholder={validMessages.address.required} />
            </FormGroup>
            <FormGroup
                label={__('Apt/Ste/Bldg')}
                name={`${prefix}_detail2`}
                checker={value => {
                    if (disabledAddress) {
                        return true;
                    }

                    if (!value.match(/^[a-zA-Z0-9 ./-]*'?[a-zA-Z0-9 ./-]*$/)) {
                        return validMessages.address.format;
                    }

                    if (value.length > 180) {
                        return validMessages.address.maxLength;
                    }

                    return true;
                }}>
                <FormControl disabled={disabledAddress} placeholder={validMessages.address.required} />
            </FormGroup>
            <FormGroup
                label={__('City')}
                name={`${prefix}_city`}
                checker={value => {
                    if (disabledAddress) {
                        return true;
                    }

                    if (!value) {
                        return validMessages.city.required;
                    }

                    if (!value.match(/^[a-zA-Z0-9 ./-]*'?[a-zA-Z0-9 ./-]*$/)) {
                        return validMessages.city.format;
                    }

                    if (value.length > 40) {
                        return validMessages.city.maxLength;
                    }

                    return true;
                }}>
                <FormControl disabled={disabledAddress} placeholder={validMessages.city.required} />
            </FormGroup>
            {isResCountryCAN ? (
                <SelectFormutil
                    label={__('Province/State')}
                    name={`${prefix}_state_code`}
                    checker={value => {
                        if (disabledAddress) {
                            return true;
                        }

                        if (isEmptyValue(value)) {
                            return __('请选择省份/州');
                        }

                        return true;
                    }}
                    selectProps={{
                        disabled: disabledAddress,
                        placeholder: __('请选择省份/州'),
                        options: dataCANProvinces?.map(item => ({ label: item, value: item })) ?? []
                    }}
                />
            ) : (
                <FormGroup
                    label={__('Province/State')}
                    name={`${prefix}_state_code`}
                    checker={value => {
                        if (disabledAddress) {
                            return true;
                        }

                        if (!value) {
                            return __('请输入省份/州');
                        }

                        if (!value.match(/^[a-zA-Z0-9 ./-]*'?[a-zA-Z0-9 ./-]*$/)) {
                            return __('You have entered the wrong format.');
                        }

                        if (value.length > 40) {
                            return printf(__('省份/州过长，不超过%s个字符'), 40);
                        }

                        return true;
                    }}>
                    <FormControl disabled={disabledAddress} placeholder={__('请输入省份/州')} />
                </FormGroup>
            )}
            <FormGroup
                label={__('Zip Code')}
                name={`${prefix}_postal`}
                checker={value => {
                    if (disabledPostal) {
                        return true;
                    }

                    if (!value) {
                        return validMessages.zip.required;
                    }

                    if (
                        (isResCountryCAN && !value.match(/^[a-zA-Z0-9]{6}$/)) ||
                        (!isResCountryCAN && !value.match(/^[a-zA-Z0-9 ./-]*'?[a-zA-Z0-9 ./-]*$/))
                    ) {
                        return validMessages.zip.format;
                    }

                    return true;
                }}>
                <FormControl disabled={disabledPostal} placeholder={validMessages.zip.required} />
            </FormGroup>
            {!hideMailAddress && <SameDeliveryAndResidence disabled={disabled} />}
        </>
    );
};

export default BaseAddress;
